/**
 * sandbox.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import {
  animate,
  Component,
  OnInit,
  state,
  style,
  transition,
  trigger,
} from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { Router } from "@angular/router";

import { jsonify } from "../support/utilities/functions";
import { SearchService } from "app/search/search.service";
import { MessagingService, MessagingState } from "app/server/messaging.service";
import {
  PingRequest,
  ReplyMessage,
  Message,
  AcknowledgmentResponse,
  ConnectionFailureMessage,
  InternalMessage,
} from "app/server/messages/general";
import { SubjectService } from "app/subjects/subject.service";
import { SoundsService } from "app/support/APIs/sounds.service";
import {
  ExtensionSignalRequest,
  ExtensionPayload,
} from "app/server/messages/collaboration/extension-signal-request";
import { AuthService } from "app/auth/auth.service";
import { interpolateString } from "app/server/validation/interpolation";
import { Variable } from "app/aggregation/typed-parameter";
import { SupportLevel, SupportService } from "./support.service";
import { Http } from "@angular/http";
import { ConnectionFailureContent } from "app/server/messages/general/PRIVATE-internal-message";
import { AlertService } from "app/common/alert.service";
import { LoginService } from "app/auth/login.service";
import { ConfigurationService } from "app/configuration/configuration.service";
import { UserService } from "app/auth/identity/user.service";
import { AlgorithmService } from "app/algorithms/management/algorithm.service";
import { CONSOLE_HISTORY } from "./console-interceptor";

enum TestStatus {
  Failed = -1,
  Unstarted = 0,
  InProgress,
  Partial,
  Success,
}

/**
 * {@link SandboxComponent} is a special page only accessible in debug-mode
 * designed as a safe place to test.
 */
@Component({
  template: `
    <div class="container">
      <div class="well">
        <p>
          This is the Advocus compatibility test page. You can use this page,
          with assistance from My Coverage Plan staff, to help diagnose issues
          with your organization's ability to access Advocus.
        </p>
        <p>
          First, wait a few seconds for any incomplete tests to run. Then either
          <a (click)="print()">print this page to PDF</a>, take screenshots of
          the entire page (often <kbd>Win</kbd>+<kbd>S</kbd> on Windows or
          <kbd>Cmd</kbd>+<kbd>Shift</kbd>+<kbd>3</kbd> on Mac), or copy
          <u>all</u> of the results text below and send it to MCP.
        </p>
      </div>
      <ul>
        <li *ngFor="let test of tests">
          <button
            class="btn"
            [class.btn-primary]="test.status === 0 || test.status === 1"
            [class.btn-danger]="test.status < 0"
            [class.btn-warning]="test.status === 2"
            [class.btn-success]="test.status === 3"
            [class.btn-disabled]="test.status >= 1"
            [disabled]="test.status >= 1"
            (click)="runTest(test)"
          >
            {{ promptByStatus(test.title, test.status) + "" }}
          </button>
          {{ test.resultsText }}
          <pre *ngIf="test.resultsObject">{{ test.resultsObject | json }}</pre>
        </li>
      </ul>
    </div>
  `,
  styles: [
    `
      li {
        margin-top: 10px;
      }
      .btn {
        width: 240px;
      }
    `,
  ],
  animations: [],
  //  [
  // 	trigger("count", [
  //       state("*", style({backgroundColor: "lightgray"})),
  //       transition("* => *", [
  //         style({backgroundColor: "yellow"}),
  //         animate(1000)
  //       ])
  //     ])
  // ]
})
export class FeatureTestRigComponent implements OnInit {
  public control: FormControl;

  public constructor(
    router: Router,
    public search: SearchService,
    public messaging: MessagingService,
    public subjects: SubjectService,
    public sounds: SoundsService,
    public auth: AuthService,
    public support: SupportService,
    public http: Http,
    public alerts: AlertService,

    public login: LoginService,
    public config: ConfigurationService,
    public user: UserService,
    public alg: AlgorithmService
  ) {
    /* DI-only */
  }

  public unreadyServices = new Set([]);
  public readyServices = new Set([]);
  private updateReadinessTest() {
    const total = this.readyServices.size + this.unreadyServices.size;
    this.tests[5] = {
      ...this.tests[5],
      status:
        this.unreadyServices.size === 0
          ? TestStatus.Success
          : this.readyServices.size > 1
          ? TestStatus.Partial
          : TestStatus.Failed,
      resultsText: `${this.readyServices.size}/${total} services ready`,
      resultsObject: {
        unready: Array.from(this.unreadyServices),
        ready: Array.from(this.readyServices),
      },
    };
  }
  private markReadiness(serviceName: string) {
    return (ready: boolean) => {
      if (ready) {
        this.unreadyServices.delete(serviceName);
        this.readyServices.add(serviceName);
      } else {
        this.readyServices.delete(serviceName);
        this.unreadyServices.add(serviceName);
      }
      this.updateReadinessTest();
    };
  }

  public promptByStatus(title: string, status: TestStatus): string {
    switch (status) {
      case TestStatus.Failed:
        return `${title}: FAILED`;
      case TestStatus.Unstarted:
        return `Run ${title}`;
      case TestStatus.InProgress:
        return `${title}: in progress...`;
      case TestStatus.Partial:
        return `${title}: partial success!`;
      case TestStatus.Success:
        return `${title}: success!`;
      default:
        return `${title}: unknown status ${status}`;
    }
  }

  public fetchTest() {
    this.tests[2].status = TestStatus.InProgress;
    const started = Date.now();
    const sub = this.http
      .get("/static/config/settings.json")
      .subscribe((res) => {
        if (res.ok) {
          this.tests[2].status = TestStatus.Success;
          this.tests[2].resultsText = "Retrieved local config JSON";
          this.tests[2].resultsObject = {
            status: res.status,
            cType: res.headers.get("content-type"),
            pby: res.headers.get("x-powered-by"),
            elapsed: Date.now() - started,
          };
        } else {
          this.tests[2].status = TestStatus.Failed;
          this.tests[2].resultsText = "Error during JSON retrieval";
          this.tests[2].resultsObject = {
            status: res.status,
            headers: res.headers.toJSON(),
            body: res.toString(),
            elapsed: Date.now() - started,
          };
        }
      });
    const maxTime = 2000;
    setTimeout(() => {
      if (sub.closed) {
        return;
      } else {
        sub.unsubscribe();
        this.tests[2].status = TestStatus.Failed;
        this.tests[2].resultsText = `Fetch took longer than ${
          maxTime / 1000
        }s to complete, assuming failed!`;
      }
    }, maxTime);
  }

  public runTest({ run }: { run?: () => void }) {
    if (run && typeof run === "function") {
      run();
    }
  }
  public tests: Array<{
    title: string;
    status: TestStatus;
    run?: () => void;
    resultsText: string;
    resultsObject?: any;
  }> = [
    {
      title: "Javascript Baseline",
      status: TestStatus.Success,
      resultsText: "Javascript is available and functioning.",
    },
    {
      title: "Browser Support",
      status:
        this.support.browserSupportLevel > SupportLevel.Known
          ? TestStatus.Partial
          : TestStatus.Success,
      resultsText:
        this.support.browserSupportLevel === SupportLevel.UnsupportedAgent
          ? "This is not a well-supported browser agent."
          : this.support.browserSupportLevel > SupportLevel.Known
          ? "This version of Chrome may not support critical features."
          : "This version of Chrome is well-supported.",
      resultsObject: this.support.browser,
    },
    {
      title: "Resource Retrieval",
      status: TestStatus.Unstarted,
      resultsText: "Running...",
      resultsObject: {},
      // run: () => this.fetchTest()
    },
    {
      title: "Primary Websocket",
      status: TestStatus.Unstarted,
      resultsText:
        "Waiting for socket service; if this message lasts more than 2 seconds, it's a failure",
      resultsObject: {
        state: this.messaging.state,
      },
    },
    {
      title: "Protocol Test",
      status:
        location.protocol === "https:" ? TestStatus.Success : TestStatus.Failed,
      resultsText:
        location.protocol === "https:"
          ? "Plausible TLS connection (unconfirmable without server)"
          : "Wrong protocol used!",
      resultsObject:
        location.protocol === "https:"
          ? undefined
          : {
              protocol: location.protocol,
              host: location.host,
              href: location.href,
            },
    },
    {
      title: "Service Readiness",
      status: TestStatus.Unstarted,
      resultsText: "",
    },
    {
      title: "Found Errors",
      status: TestStatus.InProgress,
      resultsText: "Awaiting log quiescence...",
    },
  ];

  public listErrors () {
    const errors = CONSOLE_HISTORY.filter(([e, n, c]) => {
      return e === "error";
    });
    if (errors.length > 0) {
      this.tests[6].status = TestStatus.Partial;
      this.tests[6].resultsText = `${errors.length} errors in log`;
      this.tests[6].resultsObject = errors;
    }
  }

  public ngOnInit(): void {
    this.messaging.ready.subscribe((r) => {
      console.warn(`Messaging readiness update: ${r}`);
      if (r) {
        this.tests[3] = {
          ...this.tests[3],
          status: TestStatus.Success,
          resultsText: "Websockets connected to server",
          resultsObject: {
            pingStats: this.messaging.pingStats(),
          },
        };
      } else {
        this.tests[3] = {
          ...this.tests[3],
          status: TestStatus.InProgress,
          resultsText: "Websockets attempting to connect",
          resultsObject: {
            status: this.messaging.state,
            recentTimes: this.messaging.recentLog.list(),
            pingStats: this.messaging.pingStats(),
            retries: [this.messaging.retryCount, this.messaging.restartCount],
          },
        };
      }
    });
    const RESTARTS_TIL_FAILURE = 1;
    setInterval(() => {
      if (
        this.messaging.state === MessagingState.Dead ||
        this.messaging.retryCount >= RESTARTS_TIL_FAILURE
      ) {
        this.tests[3] = {
          ...this.tests[3],
          status: TestStatus.Failed,
          resultsText: "Websocket had dropped connection",
          resultsObject: {
            status: this.messaging.state,
            recentTimes: this.messaging.recentLog.list(),
            pingStats: this.messaging.pingStats(),
            retries: [this.messaging.retryCount, this.messaging.restartCount],
          },
        };
      }
    }, 1900);

    this.alerts.mute();

    this.messaging.ready.subscribe(this.markReadiness("messaging"));
    this.config.ready.subscribe(this.markReadiness("configuration"));
    this.user.ready.subscribe(this.markReadiness("users"));
    this.alg.ready.subscribe(this.markReadiness("algorithms"));
    this.search.ready.subscribe(this.markReadiness("search"));

    this.fetchTest();

    setTimeout(() => this.listErrors(), 2000);
  }

  public print() {
    window.print();
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/debug/feature-test-rig.component.ts